// Thin client for the LTI lambda's /api/* routes. The session JWT (the
// `?s=...` token from launch) is sent as Authorization: Bearer.

export interface MeResponse {
  tenantId: string;
  tenantDisplayName: string;
  lmsType: 'canvas' | 'd2l' | 'moodle';
  role: string;
  contextId: string | null;
  lmsUserId: string | null;
  email: string | null;
  name: string | null;
  nickname: string | null;
  nameDropEnabled: boolean;
}

export interface RosterMember {
  email: string;
  name: string;
  pronouns?: string;
  recording?: string;
  org?: string;
}

export interface RosterResponse {
  orgName: string;
  currentUser: RosterMember | null;
  members: RosterMember[];
  courseScoped: boolean;
  contextId: string | null;
}

export interface ProfileUrlResponse {
  profileUrl: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getApiBase(): string {
  if (typeof window !== 'undefined' && window.__ND_SESSION__?.apiBaseUrl) {
    return window.__ND_SESSION__.apiBaseUrl;
  }
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.trim()) return fromEnv.trim();
  // Fallback to the known prod API gateway URL — keeps the build working
  // even if the env var isn't injected. CloudFormation export name is
  // `prod-api-url`.
  return 'https://lti.namedrop.io';
}

async function request<T>(
  path: string,
  init: { method: 'GET' | 'POST'; token: string; body?: unknown },
): Promise<T> {
  const headers: Record<string, string> = {
    authorization: `Bearer ${init.token}`,
  };
  const requestInit: RequestInit = { method: init.method, headers };
  if (init.body !== undefined) {
    headers['content-type'] = 'application/json';
    requestInit.body = JSON.stringify(init.body);
  }
  const res = await fetch(`${getApiBase()}${path}`, requestInit);
  if (!res.ok) {
    let parsed: { error?: string; message?: string } = {};
    try {
      parsed = (await res.json()) as { error?: string; message?: string };
    } catch {
      // ignore — fall through to status text
    }
    throw new ApiError(
      res.status,
      parsed.error ?? 'http_error',
      parsed.message ?? `${path} returned ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export function fetchMe(token: string): Promise<MeResponse> {
  return request<MeResponse>('/api/me', { method: 'GET', token });
}

export function fetchRoster(token: string): Promise<RosterResponse> {
  return request<RosterResponse>('/api/roster', { method: 'POST', token, body: {} });
}

export function fetchProfileUrl(token: string): Promise<ProfileUrlResponse> {
  return request<ProfileUrlResponse>('/api/profile-url', {
    method: 'POST',
    token,
    body: {},
  });
}
